/**
 * Admin API client for app-managed access groups.
 */
import apiService from './api';
import type { DataScope } from '../types/accessControl';

export interface AccessGroupMember {
  id?: number;
  user_key: string;
  display_name?: string | null;
}

export interface AccessGroupDataScope extends DataScope {
  id?: number;
}

export interface AccessGroupSummary {
  id: number;
  name: string;
  description?: string | null;
  is_active: boolean;
  member_count: number;
  permission_count: number;
  data_scope_count: number;
}

export interface AccessGroupDetail {
  id: number;
  name: string;
  description?: string | null;
  is_active: boolean;
  permissions: string[];
  members: AccessGroupMember[];
  data_scopes: AccessGroupDataScope[];
}

export interface AccessGroupWrite {
  name?: string;
  description?: string | null;
  is_active?: boolean;
  permissions?: string[];
  members?: Array<{ user_key: string; display_name?: string | null }>;
  data_scopes?: Array<{
    entity_type?: string | null;
    device_type?: string | null;
    course_id?: string | null;
    mode?: 'allow' | 'block' | string | null;
  }>;
}

export interface PermissionCatalog {
  modules: Array<{
    key: string;
    label: string;
    feature_flag: string;
    tabs: Array<{ key: string; label: string }>;
  }>;
  device_types: string[];
}

class AccessControlApi {
  async getCatalog(): Promise<PermissionCatalog> {
    const res = await apiService.client.get('/admin/access-control/catalog');
    return res.data;
  }

  async listGroups(): Promise<AccessGroupSummary[]> {
    const res = await apiService.client.get('/admin/access-groups');
    return res.data;
  }

  async getGroup(id: number): Promise<AccessGroupDetail> {
    const res = await apiService.client.get(`/admin/access-groups/${id}`);
    return res.data;
  }

  async createGroup(body: AccessGroupWrite & { name: string }): Promise<AccessGroupDetail> {
    const res = await apiService.client.post('/admin/access-groups', body);
    return res.data;
  }

  async updateGroup(id: number, body: AccessGroupWrite): Promise<AccessGroupDetail> {
    const res = await apiService.client.put(`/admin/access-groups/${id}`, body);
    return res.data;
  }

  async deleteGroup(id: number): Promise<void> {
    await apiService.client.delete(`/admin/access-groups/${id}`);
  }
}

export const accessControlApi = new AccessControlApi();
export default accessControlApi;
